import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SOURCE_LABELS } from "../../shared/sources";
import { brandQuery, useBrand } from "../brandContext";
import { useToast } from "../toastContext";

type Triage = "new" | "relevant" | "watch" | "junk";
type TriageFilter = "feed" | "relevant" | "watch" | "junk";

type Mention = {
  id: number;
  source: string;
  url: string;
  title: string;
  snippet: string;
  host: string;
  score: number;
  bucket: "high" | "medium" | "low";
  triage?: Triage;
  last_seen_at: string;
  created_at?: string;
  image_url?: string;
  reasons?: string | string[];
};

function reasonList(m: Mention): string[] {
  const r = m.reasons;
  if (Array.isArray(r)) return r.map(String);
  if (typeof r === "string" && r.trim()) {
    try {
      const parsed = JSON.parse(r) as unknown;
      if (Array.isArray(parsed)) return parsed.map(String);
    } catch {
      return [r];
    }
  }
  return [];
}

const FILTERS: { id: TriageFilter; label: string }[] = [
  { id: "feed", label: "Лента" },
  { id: "relevant", label: "Своё" },
  { id: "watch", label: "Отслеживаемые" },
  { id: "junk", label: "Мусор" },
];

export function MentionsPage({
  initialFilter = "feed",
}: {
  initialFilter?: TriageFilter;
}) {
  const { activeId } = useBrand();
  const [searchParams, setSearchParams] = useSearchParams();
  const sourceFilter = searchParams.get("source") || "";
  const hostFilter = searchParams.get("host") || "";
  const [hours, setHours] = useState(24 * 30);
  const [filter, setFilter] = useState<TriageFilter>(initialFilter);
  const [onlyNew, setOnlyNew] = useState(searchParams.get("new") === "1");
  const [searchText, setSearchText] = useState("");
  const [sort, setSort] = useState<"score" | "date">("score");
  const [visible, setVisible] = useState(50);
  const [items, setItems] = useState<Mention[]>([]);
  const [bySource, setBySource] = useState<{ source: string; count: number }[]>(
    [],
  );
  const [busyId, setBusyId] = useState<number | null>(null);

  const reload = useCallback(() => {
    const q = brandQuery(activeId);
    const p = new URLSearchParams(q.startsWith("?") ? q.slice(1) : q);
    p.set("hours", String(hours));
    p.set("limit", "300");
    p.set("triage", filter);
    if (sourceFilter) p.set("source", sourceFilter);
    if (hostFilter) p.set("host", hostFilter);
    if (onlyNew) p.set("new", "1");
    void Promise.all([
      fetch(`/api/mentions?${p.toString()}`).then((r) => r.json()),
      fetch(`/api/stats${q ? `${q}&` : "?"}hours=${hours}`).then((r) => r.json()),
    ]).then(([m, s]) => {
      setItems(m.items || []);
      setBySource(s.bySource || []);
    });
  }, [hours, activeId, filter, sourceFilter, hostFilter, onlyNew]);

  const isNewItem = (m: Mention) =>
    !!m.created_at && Date.now() - Date.parse(m.created_at) < 24 * 3600_000;

  useEffect(() => {
    setVisible(50);
  }, [filter, sourceFilter, hostFilter, onlyNew, hours, searchText]);

  const shown = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    let list = items;
    if (needle) {
      list = list.filter((m) =>
        [m.title, m.snippet, m.host, m.url]
          .filter(Boolean)
          .some((v) => v.toLowerCase().includes(needle)),
      );
    }
    list = [...list].sort((a, b) =>
      sort === "score"
        ? b.score - a.score
        : Date.parse(b.last_seen_at) - Date.parse(a.last_seen_at),
    );
    return list;
  }, [items, searchText, sort]);

  useEffect(() => {
    setFilter(initialFilter);
  }, [initialFilter]);

  useEffect(() => {
    reload();
  }, [reload]);

  const total = bySource.reduce((a, b) => a + b.count, 0) || 1;
  const bars = useMemo(
    () =>
      bySource.map((x) => ({
        ...x,
        pct: Math.round((x.count / total) * 100),
      })),
    [bySource, total],
  );

  const { toast } = useToast();

  async function triage(id: number, decision: Triage, scope: "item" | "host" = "item") {
    const item = items.find((m) => m.id === id);
    if (
      decision === "junk" &&
      scope === "host" &&
      !window.confirm(
        `Скрыть ВЕСЬ хост ${item?.host || ""}? Все его упоминания уйдут в мусор и не вернутся после повторных сканов.`,
      )
    ) {
      return;
    }
    setBusyId(id);
    try {
      const r = await fetch(`/api/mentions/${id}/triage`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ decision, scope }),
      });
      if (!r.ok) throw new Error(String(r.status));
      const labels: Record<Triage, string> = {
        new: "Возвращено в ленту",
        relevant: "Отмечено как своё — убрано из ленты",
        watch: "Добавлено в отслеживаемые",
        junk: scope === "host" ? `Хост ${item?.host || ""} — в мусор` : "Убрано в мусор",
      };
      toast(labels[decision], "ok");
      reload();
    } catch (e) {
      toast(`Не удалось: ${e instanceof Error ? e.message : e}`, "err");
    } finally {
      setBusyId(null);
    }
  }

  const emptyText =
    filter === "watch"
      ? "Пока ничего не отслеживается — отметьте позиции в ленте."
      : filter === "junk"
        ? "Мусора нет. Отмеченные сюда не вернутся в ленту после повторного скана."
        : filter === "relevant"
          ? "Пока нет отмеченных как своё / легитимные. Такие позиции убраны из ленты угроз."
          : "Пока пусто — нажмите «Скан» (живой поиск).";

  return (
    <>
      <div className="page-head">
        <h2>
          {filter === "watch"
            ? "Отслеживаемые"
            : filter === "junk"
              ? "Мусор"
              : filter === "relevant"
                ? "Своё / легитимное"
                : "Упоминания"}
        </h2>
        <div className="seg">
          {[
            [24, "24ч"],
            [24 * 7, "7д"],
            [24 * 30, "30д"],
          ].map(([h, label]) => (
            <button
              key={String(h)}
              className={hours === h ? "active" : ""}
              onClick={() => setHours(Number(h))}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="seg triage-tabs" style={{ marginBottom: 16 }}>
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            className={filter === f.id ? "active" : ""}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
        <button
          type="button"
          className={onlyNew ? "active chip-new" : "chip-new"}
          onClick={() => setOnlyNew((v) => !v)}
          title="Только то, что сканер увидел впервые за последние 24 часа"
        >
          Новые 24ч
        </button>
      </div>
      <div className="feed-toolbar">
        <input
          className="input feed-search"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Поиск по тексту, хосту, ссылке…"
        />
        <div className="seg">
          {(
            [
              ["score", "По скору"],
              ["date", "По дате"],
            ] as const
          ).map(([v, label]) => (
            <button
              key={v}
              type="button"
              className={sort === v ? "active" : ""}
              onClick={() => setSort(v)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {sourceFilter || hostFilter ? (
        <p className="hint" style={{ marginTop: -8, marginBottom: 14 }}>
          {sourceFilter ? (
            <>
              Источник: <strong>{SOURCE_LABELS[sourceFilter] || sourceFilter}</strong>
              {" · "}
              <button
                type="button"
                className="linkish"
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  next.delete("source");
                  setSearchParams(next);
                }}
              >
                сбросить
              </button>
            </>
          ) : null}
          {hostFilter ? (
            <>
              {sourceFilter ? " · " : ""}
              Хост: <strong>{hostFilter}</strong>
              {" · "}
              <button
                type="button"
                className="linkish"
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  next.delete("host");
                  setSearchParams(next);
                }}
              >
                сбросить
              </button>
            </>
          ) : null}
        </p>
      ) : null}

      <div className="grid-2">
        <div className="panel">
          <h3>
            Найдено {shown.length}
            {searchText ? ` (из ${items.length})` : ""}
          </h3>
          {shown.length === 0 ? (
            <div className="empty">
              {searchText ? `Ничего по запросу «${searchText}»` : emptyText}
            </div>
          ) : (
            shown.slice(0, visible).map((m) => (
              <article className="mention" key={m.id}>
                <div className="time">
                  {new Date(m.last_seen_at).toLocaleString("ru-RU", {
                    day: "2-digit",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
                <div className="mention-body">
                  {m.image_url ? (
                    <a
                      className="mention-thumb"
                      href={m.url}
                      target="_blank"
                      rel="noreferrer"
                      title="Картинка карточки"
                    >
                      <img src={m.image_url} alt="" loading="lazy" referrerPolicy="no-referrer" />
                    </a>
                  ) : null}
                  <div className="mention-main">
                  <a className="host" href={m.url} target="_blank" rel="noreferrer">
                    {m.host || m.source}
                  </a>
                  {m.host ? (
                    <button
                      type="button"
                      className="linkish host-filter"
                      title="Показать только этот хост"
                      onClick={() => {
                        const next = new URLSearchParams(searchParams);
                        next.set("host", m.host);
                        setSearchParams(next);
                      }}
                    >
                      фильтр
                    </button>
                  ) : null}
                  <div style={{ marginTop: 2 }}>
                    {isNewItem(m) ? <span className="badge-new">новое</span> : null}
                    {reasonList(m).some((r) => r.includes("image_match")) ? (
                      <span
                        className="badge-img"
                        title={reasonList(m).find((r) => r.includes("image_match"))}
                      >
                        фото≈эталон
                      </span>
                    ) : null}
                    {m.title}
                  </div>
                  <div className="snip">{m.snippet || "—"}</div>
                  <div className="triage-actions">
                    <button
                      type="button"
                      className={`triage-btn relevant ${m.triage === "relevant" ? "on" : ""}`}
                      disabled={busyId === m.id}
                      onClick={() => void triage(m.id, "relevant")}
                      title="Своё — официальный канал / легитимное упоминание бренда. Убирается из ленты угроз"
                    >
                      Своё
                    </button>
                    <button
                      type="button"
                      className={`triage-btn watch ${m.triage === "watch" ? "on" : ""}`}
                      disabled={busyId === m.id}
                      onClick={() => void triage(m.id, "watch")}
                      title="Отслеживать — отдельный список"
                    >
                      Отслеживать
                    </button>
                    <button
                      type="button"
                      className={`triage-btn junk ${m.triage === "junk" ? "on" : ""}`}
                      disabled={busyId === m.id}
                      onClick={() => void triage(m.id, "junk")}
                      title="Мусор — скрыть эту позицию и не показывать снова"
                    >
                      Мусор
                    </button>
                    {m.host ? (
                      <button
                        type="button"
                        className="triage-btn junk-host"
                        disabled={busyId === m.id}
                        onClick={() => void triage(m.id, "junk", "host")}
                        title={`Мусор для всего хоста ${m.host}`}
                      >
                        Мусор · хост
                      </button>
                    ) : null}
                    {m.triage && m.triage !== "new" ? (
                      <button
                        type="button"
                        className="triage-btn reset"
                        disabled={busyId === m.id}
                        onClick={() => void triage(m.id, "new")}
                        title="Вернуть в ленту"
                      >
                        Сброс
                      </button>
                    ) : null}
                  </div>
                  </div>
                </div>
                <div className={`pill ${m.bucket}`}>
                  <span className="d" />
                  {m.bucket} · {m.score}
                </div>
              </article>
            ))
          )}
          {shown.length > visible ? (
            <button
              type="button"
              className="btn show-more"
              onClick={() => setVisible((v) => v + 50)}
            >
              Показать ещё {Math.min(50, shown.length - visible)} из {shown.length - visible}
            </button>
          ) : null}
        </div>
        <div className="panel">
          <h3>Источники упоминаний</h3>
          {bars.length === 0 ? (
            <div className="empty">Нет данных по источникам</div>
          ) : (
            bars.map((b) => (
              <button
                type="button"
                className={`bar-row bar-row-btn${sourceFilter === b.source ? " active" : ""}`}
                key={b.source}
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  if (sourceFilter === b.source) next.delete("source");
                  else next.set("source", b.source);
                  setSearchParams(next);
                }}
              >
                <span>{SOURCE_LABELS[b.source] || b.source}</span>
                <div className="bar-track">
                  <div className="bar-fill" style={{ width: `${b.pct}%` }} />
                </div>
                <span>{b.pct}%</span>
              </button>
            ))
          )}
          <p className="hint" style={{ marginTop: 16 }}>
            «Своё» — легитимное / наше: уходит из ленты угроз, но лежит во
            вкладке «Своё». «Мусор» — скрыть навсегда. При повторном скане метка
            сохраняется.
          </p>
        </div>
      </div>
    </>
  );
}
